import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import SEO from '../components/SEO';
import { useTranslation } from '../contexts/LanguageContext';

function BlogPost() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Blog posts data with full content
  const blogPosts = {
    1: {
      id: 1,
      title: 'How to Protect Your Privacy Online with Disposable Emails',
      excerpt: 'Learn how temporary email addresses can help protect your personal information from spam and data breaches.',
      date: '2024-01-15',
      category: 'Privacy',
      readTime: '5 min read',
      author: 'FreeDisposableEmail Team',
      content: `
        <p>In today's digital age, protecting your privacy online has become more important than ever. With data breaches, spam emails, and marketing campaigns constantly bombarding your inbox, it's crucial to find ways to safeguard your personal information.</p>
        
        <h2>What Are Disposable Emails?</h2>
        <p>Disposable email addresses are temporary email accounts that you can use for one-time registrations, verifications, or any situation where you don't want to share your real email address. These emails automatically expire after a set period, typically 30 minutes to a few hours.</p>
        
        <h2>Why Use Disposable Emails?</h2>
        <p>There are several compelling reasons to use disposable email addresses:</p>
        <ul>
          <li><strong>Protect Your Privacy:</strong> Keep your real email address private and avoid unwanted marketing emails</li>
          <li><strong>Prevent Spam:</strong> Use temporary emails for one-time sign-ups to avoid spam in your main inbox</li>
          <li><strong>Security:</strong> Reduce the risk of your email being exposed in data breaches</li>
          <li><strong>Convenience:</strong> No need to create multiple accounts or manage multiple email addresses</li>
        </ul>
        
        <h2>How to Use Disposable Emails Effectively</h2>
        <p>Here are some best practices for using disposable email addresses:</p>
        <ol>
          <li><strong>For One-Time Registrations:</strong> Use disposable emails when signing up for services you're not sure about</li>
          <li><strong>For Verifications:</strong> Use them for email verification processes that you don't need long-term access to</li>
          <li><strong>For Testing:</strong> Perfect for testing websites, apps, or services without using your real email</li>
          <li><strong>For Promotions:</strong> Use when signing up for promotions or discounts that might result in marketing emails</li>
        </ol>
        
        <h2>Best Practices</h2>
        <p>When using disposable emails, remember to:</p>
        <ul>
          <li>Check your inbox immediately after generating the email</li>
          <li>Complete your registration or verification within the email's lifetime</li>
          <li>Don't use disposable emails for important accounts you'll need to access later</li>
          <li>Be aware that emails expire automatically, so act quickly</li>
        </ul>
        
        <h2>Conclusion</h2>
        <p>Disposable email addresses are a powerful tool for protecting your privacy online. By using temporary emails for one-time registrations and verifications, you can keep your real email address safe from spam, data breaches, and unwanted marketing campaigns.</p>
        
        <p>At FreeDisposableEmail.com, we make it easy to generate temporary email addresses instantly, with no registration required. Try it today and take control of your online privacy!</p>
      `
    },
    2: {
      id: 2,
      title: '10 Best Practices for Using Temporary Email Services',
      excerpt: 'Discover the best ways to use disposable email addresses safely and effectively for your online activities.',
      date: '2024-01-10',
      category: 'Tips',
      readTime: '4 min read',
      author: 'FreeDisposableEmail Team',
      content: `
        <p>Using temporary email services can significantly enhance your online privacy and security. However, to get the most out of these services, it's important to follow best practices. Here are 10 essential tips for using disposable email addresses effectively.</p>
        
        <h2>1. Use for One-Time Registrations</h2>
        <p>Disposable emails are perfect for one-time registrations where you don't need long-term access. This includes signing up for newsletters, downloading resources, or accessing trial services.</p>
        
        <h2>2. Check Your Inbox Immediately</h2>
        <p>Since temporary emails expire quickly (usually within 30 minutes), make sure to check your inbox immediately after generating the email. Don't wait too long or you might miss important verification emails.</p>
        
        <h2>3. Don't Use for Important Accounts</h2>
        <p>Avoid using disposable emails for accounts you'll need to access later, such as banking, social media, or work-related accounts. These require long-term email access.</p>
        
        <h2>4. Use Custom Usernames When Possible</h2>
        <p>Many services allow you to create custom usernames for your temporary email. This makes it easier to remember and identify which service you used it for.</p>
        
        <h2>5. Be Aware of Expiration Times</h2>
        <p>Different services have different expiration times. Make sure you know how long your email will remain active and complete any necessary actions within that timeframe.</p>
        
        <h2>6. Use for Testing and Development</h2>
        <p>Disposable emails are excellent for testing websites, applications, or email functionality without cluttering your main inbox.</p>
        
        <h2>7. Protect Against Spam</h2>
        <p>Use temporary emails when signing up for services that might send promotional emails or newsletters. This keeps your main inbox clean.</p>
        
        <h2>8. Verify Email Deliverability</h2>
        <p>If you're testing email functionality, disposable emails can help verify that emails are being sent and received correctly.</p>
        
        <h2>9. Don't Share Sensitive Information</h2>
        <p>While disposable emails protect your real address, remember that the emails themselves are temporary and may be accessible to others. Don't use them for highly sensitive communications.</p>
        
        <h2>10. Choose a Reliable Service</h2>
        <p>Select a reputable temporary email service that offers real-time inbox updates, secure connections, and reliable email delivery. FreeDisposableEmail.com provides all these features and more.</p>
        
        <h2>Conclusion</h2>
        <p>By following these best practices, you can maximize the benefits of temporary email services while maintaining your privacy and security online. Remember, disposable emails are a tool for convenience and privacy, not a replacement for your primary email for important communications.</p>
      `
    },
    3: {
      id: 3,
      title: 'Understanding Email Privacy: Why It Matters',
      excerpt: 'Explore the importance of email privacy in today\'s digital world and how disposable emails can help.',
      date: '2024-01-05',
      category: 'Privacy',
      readTime: '6 min read',
      author: 'FreeDisposableEmail Team',
      content: `
        <p>Email privacy is a critical concern in our increasingly connected world. Every day, millions of emails are sent and received, and many of them contain sensitive personal information. Understanding why email privacy matters and how to protect it is essential for anyone who uses email regularly.</p>
        
        <h2>The Importance of Email Privacy</h2>
        <p>Your email address is one of the most valuable pieces of personal information you have online. It's used for:</p>
        <ul>
          <li>Account verification and password resets</li>
          <li>Receiving important communications</li>
          <li>Online shopping and transactions</li>
          <li>Social media and networking</li>
          <li>Professional communications</li>
        </ul>
        
        <h2>Risks to Email Privacy</h2>
        <p>There are several ways your email privacy can be compromised:</p>
        <ul>
          <li><strong>Data Breaches:</strong> When companies you've signed up with experience data breaches, your email address may be exposed</li>
          <li><strong>Spam and Marketing:</strong> Companies often sell email lists to marketers, leading to unwanted emails</li>
          <li><strong>Phishing Attacks:</strong> Cybercriminals use email addresses to send phishing emails and attempt identity theft</li>
          <li><strong>Tracking:</strong> Some services track your email activity to build profiles about you</li>
        </ul>
        
        <h2>How Disposable Emails Help</h2>
        <p>Disposable email addresses provide a layer of protection by:</p>
        <ul>
          <li>Keeping your real email address private</li>
          <li>Preventing spam from reaching your main inbox</li>
          <li>Reducing your exposure in data breaches</li>
          <li>Allowing you to test services without commitment</li>
          <li>Providing temporary access for one-time verifications</li>
        </ul>
        
        <h2>Best Practices for Email Privacy</h2>
        <p>To protect your email privacy:</p>
        <ol>
          <li>Use disposable emails for one-time registrations</li>
          <li>Be selective about where you share your real email address</li>
          <li>Use strong, unique passwords for email accounts</li>
          <li>Enable two-factor authentication when available</li>
          <li>Regularly review and clean up your email subscriptions</li>
          <li>Be cautious about clicking links in emails from unknown senders</li>
        </ol>
        
        <h2>The Future of Email Privacy</h2>
        <p>As technology evolves, so do the threats to email privacy. However, tools like disposable email services are becoming more sophisticated and user-friendly, making it easier than ever to protect your personal information online.</p>
        
        <h2>Conclusion</h2>
        <p>Email privacy is not just about avoiding spam—it's about protecting your personal information, reducing your risk of identity theft, and maintaining control over your digital identity. By understanding the risks and using tools like disposable email addresses, you can take proactive steps to protect your privacy online.</p>
        
        <p>At FreeDisposableEmail.com, we're committed to helping you protect your email privacy. Our service is free, requires no registration, and provides instant temporary email addresses whenever you need them.</p>
      `
    },
    4: {
      id: 4,
      title: 'The Future of Email Security and Privacy',
      excerpt: 'A look at emerging trends in email security and how temporary email services are evolving.',
      date: '2024-01-01',
      category: 'Technology',
      readTime: '7 min read',
      author: 'FreeDisposableEmail Team',
      content: `
        <p>The landscape of email security and privacy is constantly evolving. As cyber threats become more sophisticated, so do the tools and strategies we use to protect ourselves. Let's explore the emerging trends in email security and how temporary email services are adapting to meet future challenges.</p>
        
        <h2>Current State of Email Security</h2>
        <p>Today's email security landscape faces numerous challenges:</p>
        <ul>
          <li>Increasingly sophisticated phishing attacks</li>
          <li>Ransomware and malware distribution via email</li>
          <li>Data breaches exposing millions of email addresses</li>
          <li>Email tracking and surveillance</li>
          <li>Spam and unwanted marketing emails</li>
        </ul>
        
        <h2>Emerging Technologies</h2>
        <p>Several technologies are shaping the future of email security:</p>
        <ul>
          <li><strong>AI-Powered Filtering:</strong> Machine learning algorithms that can detect and filter malicious emails more effectively</li>
          <li><strong>End-to-End Encryption:</strong> Enhanced encryption methods to protect email content</li>
          <li><strong>Blockchain Verification:</strong> Using blockchain to verify email authenticity</li>
          <li><strong>Zero-Trust Architecture:</strong> Security models that verify every email interaction</li>
        </ul>
        
        <h2>The Role of Disposable Emails</h2>
        <p>Disposable email services are evolving to provide better security and privacy:</p>
        <ul>
          <li><strong>Real-Time Updates:</strong> Instant notifications when emails arrive</li>
          <li><strong>Auto-Expiry:</strong> Automatic deletion to minimize data exposure</li>
          <li><strong>No Registration Required:</strong> Enhanced privacy through anonymity</li>
          <li><strong>Secure Connections:</strong> HTTPS and encrypted communications</li>
          <li><strong>Multiple Domain Support:</strong> More options for users</li>
        </ul>
        
        <h2>Future Trends</h2>
        <p>Looking ahead, we can expect to see:</p>
        <ol>
          <li><strong>Enhanced Privacy Features:</strong> More advanced privacy controls and options</li>
          <li><strong>Better Integration:</strong> Easier integration with other privacy tools</li>
          <li><strong>Improved User Experience:</strong> More intuitive interfaces and faster performance</li>
          <li><strong>Advanced Filtering:</strong> Better spam and malware detection</li>
          <li><strong>Mobile Optimization:</strong> Enhanced mobile experiences</li>
        </ol>
        
        <h2>Best Practices for the Future</h2>
        <p>To stay ahead of email security threats:</p>
        <ul>
          <li>Use disposable emails for all non-essential registrations</li>
          <li>Keep your main email address private and secure</li>
          <li>Stay informed about the latest security threats</li>
          <li>Use strong, unique passwords</li>
          <li>Enable multi-factor authentication</li>
          <li>Regularly review your email security settings</li>
        </ul>
        
        <h2>Conclusion</h2>
        <p>The future of email security and privacy is bright, with new technologies and tools emerging to help protect users. Disposable email services like FreeDisposableEmail.com are at the forefront of this evolution, providing innovative solutions to protect your privacy online.</p>
        
        <p>As threats evolve, so will our defenses. By staying informed and using the right tools, you can protect your email privacy both now and in the future.</p>
      `
    }
  };

  const post = blogPosts[id];

  if (!post) {
    return (
      <div className="min-h-screen bg-gray-50">
        <SEO
          title="Blog Post Not Found - FreeDisposableEmail.com"
          description="The blog post you're looking for doesn't exist."
          noindex={true}
        />
        <Navigation />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Post Not Found</h1>
            <p className="text-gray-600 mb-6">The blog post you're looking for doesn't exist.</p>
            <Link
              to="/blog"
              className="inline-block bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all"
            >
              Back to Blog
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.excerpt,
    "image": "https://freedisposableemail.com/assets/img/logo/freedisposibleemail-logo.png",
    "datePublished": post.date,
    "dateModified": post.date,
    "author": {
      "@type": "Organization",
      "name": post.author
    },
    "publisher": {
      "@type": "Organization",
      "name": "FreeDisposableEmail.com",
      "logo": {
        "@type": "ImageObject",
        "url": "https://freedisposableemail.com/assets/img/logo/freedisposibleemail-logo.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://freedisposableemail.com/blog/${post.id}`
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title={`${post.title} - FreeDisposableEmail.com Blog`}
        description={post.excerpt}
        keywords={`${post.category.toLowerCase()}, temporary email, disposable email, email privacy, ${post.title.toLowerCase()}`}
        type="article"
        structuredData={structuredData}
      />
      <Navigation />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="mb-6 flex items-center text-primary-600 hover:text-primary-700 transition-colors"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          {t('blog.readMore') ? 'Back to Blog' : 'Back'}
        </button>

        {/* Article */}
        <article className="bg-white rounded-lg shadow-md overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-8 md:p-12">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 bg-white/20 text-white text-xs font-semibold rounded-full">
                {post.category}
              </span>
              <span className="text-sm text-white/80">{post.readTime}</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold mb-4">{post.title}</h1>
            <div className="flex items-center gap-4 text-sm text-white/90">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span>{post.author}</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <time dateTime={post.date}>
                  {new Date(post.date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </time>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-8 md:p-12">
            <div
              className="prose prose-lg max-w-none text-justify
                prose-headings:font-bold prose-headings:text-gray-900 prose-headings:mt-8 prose-headings:mb-4 first:prose-headings:mt-0
                prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-5 prose-h2:pb-2 prose-h2:border-b prose-h2:border-gray-200
                prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-4
                prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-6 prose-p:text-base
                prose-ul:my-6 prose-ul:space-y-2 prose-ul:text-gray-700
                prose-ol:my-6 prose-ol:space-y-2 prose-ol:text-gray-700
                prose-li:text-gray-700 prose-li:leading-relaxed prose-li:text-base prose-li:ml-1
                prose-strong:text-gray-900 prose-strong:font-semibold
                prose-a:text-primary-600 prose-a:no-underline prose-a:font-medium hover:prose-a:underline hover:prose-a:text-primary-700
                prose-code:text-primary-600 prose-code:bg-gray-100 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded
                prose-pre:bg-gray-900 prose-pre:text-gray-100 prose-pre:rounded-lg prose-pre:p-4
                prose-blockquote:border-l-4 prose-blockquote:border-primary-500 prose-blockquote:pl-4 prose-blockquote:italic prose-blockquote:text-gray-600"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 px-8 md:px-12 py-6 bg-gray-50">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <div>
                <p className="text-sm text-gray-600 mb-2">Share this article:</p>
                <div className="flex gap-3">
                  <a
                    href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(window.location.href)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-600 hover:text-primary-600 transition-colors"
                    aria-label="Share on Twitter"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                    </svg>
                  </a>
                  <a
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-600 hover:text-primary-600 transition-colors"
                    aria-label="Share on Facebook"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                    </svg>
                  </a>
                  <a
                    href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-600 hover:text-primary-600 transition-colors"
                    aria-label="Share on LinkedIn"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                    </svg>
                  </a>
                </div>
              </div>
              <Link
                to="/blog"
                className="text-primary-600 hover:text-primary-700 font-medium flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
                View All Posts
              </Link>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
}

export default BlogPost;
